import { format, isToday, isYesterday, parseISO } from 'date-fns';

// Format date section header
export const getDateSectionLabel = (dateKey: string): string => {
  const date = parseISO(dateKey);
  if (isToday(date)) {
    return 'Today';
  }
  if (isYesterday(date)) {
    return 'Yesterday';
  }
  return format(date, 'MMMM do, yyyy');
};

// Return the duration of a single event in minutes.
// When roundUp is true, partial minutes round up (e.g. 30s -> 1m, 60s -> 1m);
// otherwise the partial minute is truncated.
const getEventDurationMinutes = <T extends { startTime: number; endTime: number }>(
  event: T,
  roundUp = true
): number => {
  const { startTime, endTime } = event;
  const durationMins = Math.max(0, endTime - startTime) / 60000; // Convert ms to minutes
  if (roundUp) {
    return Math.ceil(durationMins);
  }
  return Math.floor(durationMins);
};

export const getEventsDurationMinutes = <T extends { startTime: number; endTime: number }>(
  events: T[],
  roundUp = true
): number => {
  if (!events.length) {
    return 0;
  }

  // Calculate the sum of individual event durations (with rounding)
  const sumOfIndividualDurations = events.reduce(
    (acc, event) => acc + getEventDurationMinutes(event, roundUp),
    0
  );

  // Calculate the actual time span from earliest start to latest end
  const earliestStart = Math.min(...events.map((event) => event.startTime));
  const latestEnd = Math.max(...events.map((event) => event.endTime));
  const actualSpanMs = latestEnd - earliestStart;
  const actualSpanMinutes = actualSpanMs / 60000;
  const roundedMins = roundUp
    ? Math.ceil(actualSpanMinutes)
    : Math.floor(actualSpanMinutes);

  // Return the minimum to ensure we never exceed the actual time span
  return Math.min(sumOfIndividualDurations, roundedMins);
};

export const getEventsDurationLabel = <T extends { startTime: number; endTime: number }>(
  events: T[],
  roundUp = true
): string => {
  const durationMinutes = getEventsDurationMinutes(events, roundUp);

  // When roundUp is false and duration is 0, check if there's actual duration less than 1 minute
  if (!roundUp && durationMinutes === 0 && !!events.length) {
    const earliestStart = Math.min(...events.map((event) => event.startTime));
    const latestEnd = Math.max(...events.map((event) => event.endTime));
    const actualSpanMs = latestEnd - earliestStart;
    if (actualSpanMs > 0 && actualSpanMs < 60000) {
      return '<1m';
    }
    // If there's no actual duration, return '0m' or empty string
    return '0m';
  }

  if (durationMinutes >= 60) {
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    return `${hours}h ${minutes}m`;
  }

  return `${durationMinutes}m`;
};
